import { useCallback, useEffect, useLayoutEffect, useMemo, useRef, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import {
    Badge, Box, Button, Center, Flex, HStack, Heading, Icon, IconButton, Image,
    Input, InputGroup, InputLeftElement, Modal, ModalBody, ModalCloseButton, ModalContent, ModalOverlay,
    Select, Spinner, Switch, Text, VStack, useToast,
} from '@chakra-ui/react';
import { TransformComponent, TransformWrapper } from 'react-zoom-pan-pinch';
import { FiArrowLeft, FiChevronDown, FiInbox, FiMessageSquare, FiPlus, FiSearch, FiSend, FiX } from 'react-icons/fi';
import { authAPI } from '../services/api';
import { useAuth } from '../context/AuthContext';
import useSocketReconnect from '../hooks/useSocketReconnect';
import { useChatEnabledStatus } from '../mobile/hooks/useChatEnabled';
import useChatSocket from '../mobile/hooks/useChatSocket';
import useOptimisticSend from '../mobile/hooks/useOptimisticSend';
import { notifyChatRead } from '../mobile/hooks/useUnreadSummary';
import useStickToBottom from '../hooks/useStickToBottom';
import MessageList from '../mobile/chat/MessageList';
import Composer from '../mobile/chat/Composer';
import RecallConfirmDialog from '../mobile/chat/RecallConfirmDialog';
import ClassPickerDialog from '../mobile/chat/ClassPickerDialog';
import { formatTime } from '../utils/dateUtils';

const PAGE_SIZE = 30;

// 對話室本地快取（2026-09-01 效能修復）：同一個對話短時間內重新開啟（切分頁再切回來），
// 先顯示上次看到的內容，不轉圈，背景打 API 確認有沒有新訊息、有差異才更新。
// 與 MobileChatRoom.jsx 是同一套邏輯，逐字對齊（該檔已為輪詢/回前景磨過一輪的既有慣例）。
// 刻意用記憶體變數、不持久化到 storage——重新整理回到空狀態才符合預期，這只是同一次
// 使用期間的暫存。cap 上限只是防禦性措施，不是效能瓶頸本身。
const ROOM_CACHE_CAP = 30;
const roomMessageCache = new Map(); // convId -> { messages, hasMore }
function cacheRoomMessages(convId, messages, hasMore) {
    roomMessageCache.delete(convId);
    roomMessageCache.set(convId, { messages, hasMore });
    if (roomMessageCache.size > ROOM_CACHE_CAP) {
        roomMessageCache.delete(roomMessageCache.keys().next().value);
    }
}

const ADMIN_ROLES = ['super_admin', 'admin', 'receptionist'];
// 同時開著的對話上限。右欄在 1440px 螢幕上約 1000px 寬，一個分頁要 ~120px 才讀得出學生姓名；
// 再多就只剩省略號，分頁本身就失去意義了。超過時擠掉最舊的那一個。
const MAX_TABS = 8;

// ─────────────────────────────────────────────────────────────────────────────
// 桌面版親師對話（取代側欄「聯絡簿紀錄」的入口）。
//
// 【為什麼直接複用 src/mobile/chat/ 的元件】
// MessageList / MessageBubble / CommBookCard / Composer 已經被老師 PWA 與家長 App 兩端共用，
// 樂觀更新、圖片放大、聯絡簿卡片、已讀回條這些行為都在上面磨過好幾輪。桌面若另寫一套，
// 等於同一個功能維護三份、三邊行為遲早漂移。所以這一頁只負責「版面」與「資料流」，
// 訊息本身的呈現一律沿用既有元件。
//
// 【配色注意】那些元件內部用 onCanvas.* 前景色，那是為手機深綠畫布(#8A9A7B)設計的近黑階。
// 落在桌面淺米底(neutral.50)上會比原本設計「更深」，但仍然可讀（近黑對淺米對比更高）。
// 刻意不改共用元件——它們被手機兩端共用，改了會連帶影響 App。改的是這裡：訊息區給一個
// 明確的 neutral.50 底、外面包白色卡片，讓白色氣泡（對方）與畫布分得開。
// ─────────────────────────────────────────────────────────────────────────────

// 旗標關閉時後端 /chat/* 一律 403（見 app/routers/chat.py _require_chat_enabled）。
// 與老師 PWA 收件匣同一種處理：顯示說明而不是讓老師打一則永遠送不出去的訊息。
function ChatDisabledNotice() {
    return (
        <Center h="100%" minH="320px" flexDirection="column" gap={2}>
            <Icon as={FiMessageSquare} boxSize="36px" color="neutral.300" />
            <Text fontSize="sm" color="neutral.500">親師對話尚未啟用</Text>
            <Text fontSize="xs" color="neutral.400">功能已就緒，待系統開啟後即可在此與家長溝通</Text>
        </Center>
    );
}

// 左欄的一列：一位**家長**＝一條對話（2026-08-19 分串後；原本是一位學生一條）。
// 未讀時把姓名加粗並在右側掛數字，讓老師掃一眼就知道哪幾個家庭在等回覆
//（這是收件匣唯一要回答的問題）。
//
// 【為什麼一定要把家長姓名畫出來】分串後同一位學生會出現兩列，其他欄位完全相同。
// 不寫出對面是誰，老師在點進去之前無從分辨——那比原本共用一條還糟，因為現在是兩個
// 看起來一模一樣的入口。user 的原始需求就是「要清楚知道對話框是誰」。
function ConversationRow({ conv, isActive, onClick }) {
    const hasUnread = conv.unread > 0;
    return (
        <HStack
            onClick={onClick}
            spacing={3}
            px={3}
            py={2.5}
            align="flex-start"
            cursor="pointer"
            borderLeft="3px solid"
            borderLeftColor={isActive ? 'brand.500' : 'transparent'}
            bg={isActive ? 'brand.50' : 'transparent'}
            _hover={{ bg: isActive ? 'brand.50' : 'neutral.100' }}
            transition="background 0.15s"
        >
            <Center
                flexShrink={0} boxSize="38px" borderRadius="full"
                bg={hasUnread ? 'brand.100' : 'neutral.100'}
                color={hasUnread ? 'brand.700' : 'neutral.500'}
                fontWeight="bold" fontSize="sm"
            >
                {(conv.student_name || '?').slice(0, 1)}
            </Center>
            <Box flex="1" minW={0}>
                <Flex justify="space-between" align="baseline" gap={2}>
                    <Text fontSize="sm" fontWeight={hasUnread ? 'bold' : 'medium'} color="neutral.800" noOfLines={1}>
                        {conv.student_name || '（未命名學生）'}
                    </Text>
                    <Text fontSize="10px" color="neutral.400" flexShrink={0}>
                        {conv.last_message_at ? formatTime(conv.last_message_at) : ''}
                    </Text>
                </Flex>
                {/* 班級與家長同一行：兩者合起來才唯一定位一條對話。家長姓名用較深的
                    neutral.600（班名維持 400），因為它是這一列與「隔壁那一列」唯一的差別。
                    舊資料可能沒有 parent_name，這時只顯示班名、版面不塌。 */}
                <Text fontSize="11px" color="neutral.400" noOfLines={1}>
                    {conv.class_name}
                    {conv.parent_name && (
                        <Text as="span" color="neutral.600" fontWeight="medium">
                            {conv.class_name ? '・' : ''}{conv.parent_name}
                        </Text>
                    )}
                </Text>
                <Flex justify="space-between" align="center" gap={2} mt={0.5}>
                    <Text fontSize="xs" color="neutral.500" noOfLines={1} flex="1">
                        {conv.last_message_preview || '尚無訊息'}
                    </Text>
                    {hasUnread && (
                        <Badge colorScheme="red" borderRadius="full" fontSize="10px" px={1.5} flexShrink={0}>
                            {conv.unread}
                        </Badge>
                    )}
                </Flex>
            </Box>
        </HStack>
    );
}

// 「尚未開始對話」那一區每一列的身分。分串後一列＝(學生, 班級, 家長)，三者缺一都會
// 讓兩列共用同一個 key——React 的 key 撞號會渲染錯，而「正在開啟中」的轉圈也會跑到別列去。
// classId 可能還沒決定（沒選班級時），用 'auto' 佔位。
const pendingRowKey = (row) => `${row.studentId}:${row.classId ?? 'auto'}:${row.parentId ?? 'none'}`;

// 左欄下半段的一列：一位「還沒有對話」的家長（來自班級名冊，不是收件匣）。
// 刻意跟 ConversationRow 長得不一樣（虛線頭像、灰字、右側一個 ＋），否則老師會把它誤認成
// 一條「已存在但還沒人講話」的對話，也就看不出點下去會發生什麼事。
function StudentRow({ row, isOpening, onClick }) {
    return (
        <HStack
            onClick={isOpening ? undefined : onClick}
            spacing={3}
            px={3}
            py={2}
            align="center"
            cursor={isOpening ? 'progress' : 'pointer'}
            borderLeft="3px solid"
            borderLeftColor="transparent"
            _hover={{ bg: 'neutral.100' }}
            transition="background 0.15s"
            opacity={isOpening ? 0.6 : 1}
        >
            <Center
                flexShrink={0} boxSize="32px" borderRadius="full"
                border="1px dashed" borderColor="neutral.300"
                color="neutral.500" fontWeight="bold" fontSize="xs"
            >
                {(row.name || '?').slice(0, 1)}
            </Center>
            <Box flex="1" minW={0}>
                <Text fontSize="sm" color="neutral.700" noOfLines={1}>
                    {row.name || '（未命名學生）'}
                    {/* 家長姓名要寫出來：一位學生會展開成爸爸、媽媽兩列，不寫的話兩列一模一樣。
                        沒有綁定家長時明說原因，否則老師只會反覆點一個永遠開不起來的列。 */}
                    <Text as="span" fontSize="11px" color={row.parentId ? 'neutral.500' : 'red.400'} ml={1.5}>
                        {row.parentName || (row.parentId ? '' : '（尚未綁定家長）')}
                    </Text>
                </Text>
                <Text fontSize="11px" color="neutral.400" noOfLines={1}>{row.className}</Text>
            </Box>
            {isOpening
                ? <Spinner size="xs" color="neutral.400" flexShrink={0} />
                : <Icon as={FiPlus} color="neutral.400" boxSize="14px" flexShrink={0} />}
        </HStack>
    );
}

// 右欄上方的分頁列（瀏覽器分頁的概念）。老師常常是「正在回 A，B 又傳訊息進來」，一次只能開
// 一條就得反覆回左欄找人；分頁把「手邊正在處理的那幾條」留著。
//
// 未讀數直接取用收件匣那一列的 conv.unread，分頁不自己維護一份：那份資料本來就被 WS 事件與
// 已讀回報持續刷新（見 useChatSocket(fetchInbox) / useSocketReconnect(fetchInbox)），
// 自己再算一套只會多出一個會跟它對不上的來源。
function ChatTabBar({ tabs, activeId, onSelect, onClose }) {
    return (
        <Flex
            flexShrink={0}
            bg="neutral.50"
            borderBottom="1px solid"
            borderColor="neutral.200"
            // 橫向捲而不換行：換行會讓右欄標頭的高度跳動，底下的訊息區跟著上下位移，
            // 正在讀的那一行會突然移位。
            overflowX="auto"
            sx={{
                '&::-webkit-scrollbar': { height: '4px' },
                '&::-webkit-scrollbar-thumb': { background: 'rgba(28, 25, 20, 0.20)', borderRadius: '2px' },
            }}
        >
            {tabs.map((t) => {
                const isActive = t.id === activeId;
                return (
                    <HStack
                        key={t.id}
                        onClick={() => onSelect(t.id)}
                        spacing={1.5}
                        px={3}
                        py={2}
                        flexShrink={0}
                        maxW="180px"
                        cursor="pointer"
                        bg={isActive ? 'white' : 'transparent'}
                        borderRight="1px solid"
                        borderColor="neutral.200"
                        // 使用中的那個分頁用底線標示，而不是只靠底色：分頁列與右欄的白底只差
                        // 一階（neutral.50 對 white，ΔL*2.1），單靠底色分不出來哪個是使用中。
                        borderBottom="2px solid"
                        borderBottomColor={isActive ? 'brand.500' : 'transparent'}
                        _hover={{ bg: isActive ? 'white' : 'neutral.100' }}
                        transition="background 0.15s"
                    >
                        {/* 分頁標籤改成兩行：分串後同一位學生會有兩個分頁，只寫學生姓名的話
                            兩個分頁在視覺上完全一樣，「切到另一位家長」變成純猜測。
                            家長姓名寧可讓分頁高一點也不能省——分頁列的存在意義就是快速切換，
                            切之前必須看得出要切去哪。 */}
                        <Box minW={0}>
                            <Text
                                fontSize="xs"
                                fontWeight={isActive ? 'bold' : 'medium'}
                                color={isActive ? 'neutral.800' : 'neutral.600'}
                                noOfLines={1}
                            >
                                {t.student_name || '（未命名學生）'}
                            </Text>
                            {t.parent_name && (
                                <Text fontSize="10px" color="neutral.400" noOfLines={1}>
                                    {t.parent_name}
                                </Text>
                            )}
                        </Box>
                        {t.unread > 0 && (
                            <Badge colorScheme="red" borderRadius="full" fontSize="9px" px={1.5} flexShrink={0}>
                                {t.unread}
                            </Badge>
                        )}
                        <Box
                            as="button"
                            type="button"
                            aria-label={`關閉 ${t.student_name || '這位學生'} 的分頁`}
                            // stopPropagation：不擋的話這一下會沿著 DOM 冒泡到外層 HStack 的
                            // onSelect，變成「關掉的同時又把它切成使用中」。
                            onClick={(e) => { e.stopPropagation(); onClose(t.id); }}
                            flexShrink={0}
                            lineHeight={0}
                            color="neutral.400"
                            _hover={{ color: 'neutral.700' }}
                        >
                            <Icon as={FiX} boxSize="12px" />
                        </Box>
                    </HStack>
                );
            })}
        </Flex>
    );
}

// 右欄：單一條對話的完整功能（分頁載入舊訊息、標記已讀、WS 即時、樂觀更新送出、圖片放大）。
// 資料流刻意逐項對齊 src/mobile/pages/MobileChatRoom.jsx——那邊每一段都是實機回報改出來的，
// 桌面若自己重推一次只會把同樣的坑再踩一遍。差別只在版面（雙欄、無安全區、有標頭）。
//
// ⚠ 由上層以 key={conv.id} 掛載，所以「換一條對話」＝整個元件重建。
// 因此這裡不需要處理 convId 變動：所有 state 天然回到初始值，也不必在 effect 裡同步 setState
// 把上一條的訊息清掉（那正是 react-hooks/set-state-in-effect 會擋的寫法）。
function ChatRoomPane({ conv, onBack, onRead }) {
    const convId = conv.id;
    const { user } = useAuth();

    // null ＝ 首次載入尚未完成（不另設 loading 布林：元件是每條對話重建一次，
    // 初始值天然就是 null，省掉在 effect 裡同步 setLoading(true) 的那一步）。
    // 【2026-09-01 效能修復】初始值改先查本地快取（roomMessageCache）：有快取的話直接
    // 顯示上次看到的內容（不是 null，不轉圈），背景仍照樣打 API 驗證，成功後覆蓋畫面並
    // 更新快取——同一個對話切分頁再切回來不必每次空等。用惰性初始化函式而非直接讀值，
    // 是因為這個元件本來就是「每條對話重建一次」（見上方 key={conv.id} 說明），初始值
    // 在這裡算一次就精準對應到「這次掛載是哪一條對話」，不需要額外的 effect 去同步。
    const [messages, setMessages] = useState(() => roomMessageCache.get(convId)?.messages ?? null);
    const [loadingMore, setLoadingMore] = useState(false);
    const [hasMore, setHasMore] = useState(() => roomMessageCache.get(convId)?.hasMore ?? false);
    const [lightbox, setLightbox] = useState(null);
    // hover 出現的收回鈕（桌面）／長按（觸控筆電）點下去先放進這裡＝確認框打開；null＝關閉
    const [recallTarget, setRecallTarget] = useState(null);
    const scrollerRef = useRef(null);
    const loading = messages === null;

    // 訊息區維持釘在最新訊息（圖片載入撐開內容時也要跟著），除非老師主動往上翻
    useStickToBottom(scrollerRef);
    // 「載入更早訊息」是往上 prepend，不該把畫面拉回最底部（下方捲動 effect 依此跳過一次）
    const skipScrollRef = useRef(false);

    // onRead 會隨上層的 scope 改變而換新的函式參考（管理員身分確認後 scope 會從 mine 變成
    // all）。放進 effect 相依會讓那一次變動連帶重跑初始載入＋重打一次已讀，所以改用 ref
    // 取最新值，effect 只依 convId。
    const onReadRef = useRef(onRead);
    useLayoutEffect(() => { onReadRef.current = onRead; });

    const appendMessage = useCallback((incoming) => {
        setMessages((prev) => {
            const list = prev || [];
            return list.some((m) => m.id === incoming.id) ? list : [...list, incoming];
        });
    }, []);

    // 訊息被收回：就地換掉同 id 的那一則（後端回的版本 text/image_url 已清空、is_recalled=true）。
    // 不能走 appendMessage——那支是「沒見過才加進去」，同一個 id 只會被去重掉，畫面仍是舊內容。
    const replaceMessage = useCallback((updated) => {
        if (!updated?.id) return;
        setMessages((prev) => (prev || []).map((m) => (m.id === updated.id ? updated : m)));
    }, []);

    // 樂觀更新送出（比照 LINE）：按下當下訊息就出現，成功後換成後端真訊息並由 appendMessage 去重。
    // 宣告位置與 MobileChatRoom 一致（appendMessage 之後、捲動 effect 之前）：捲動 effect 要讀
    // pending.length，放到檔案後段會 TDZ。
    const { pending, send: handleSend, retry: handleRetry } = useOptimisticSend({
        convId,
        user,
        appendMessage,
        // 真訊息一到就丟掉樂觀副本。WS 是獨立管道、可能先於 POST 回應抵達，不對帳會顯示重複訊息。
        messages,
    });

    // 初始載入 + 進頁即標記已讀。
    // 【2026-09-01 效能修復】失敗時只在「沒有快取可退回」才清空畫面——已經顯示的快取
    // 內容比空白更有用，這次請求失敗不該把它抹掉（見上方 useState 惰性初始值的說明）。
    useEffect(() => {
        let alive = true;
        const hadCache = roomMessageCache.has(convId);
        authAPI.chat.messages(convId, { limit: PAGE_SIZE })
            .then((r) => {
                if (!alive) return;
                const data = r.data || [];
                const hasMoreNow = data.length === PAGE_SIZE;
                setMessages(data);
                setHasMore(hasMoreNow);
                cacheRoomMessages(convId, data, hasMoreNow);
            })
            .catch(() => { if (alive && !hadCache) setMessages([]); });
        // 標記已讀後同時通知：① 底部導航/首頁紅點（window 事件）② 左欄收件匣重抓未讀數
        authAPI.chat.read(convId)
            .then(() => { notifyChatRead(); onReadRef.current?.(); })
            .catch(() => {});
        return () => { alive = false; };
    }, [convId]);

    useEffect(() => {
        if (skipScrollRef.current) {
            skipScrollRef.current = false;
            return;
        }
        // 直接設訊息區自己的 scrollTop，不用 scrollIntoView：後者會沿著祖先鏈一路捲，
        // 桌面外殼（AdminLayout 的內容區也是 overflowY:auto）會被一起捲走。
        const el = scrollerRef.current;
        if (el) {
            el.scrollTop = el.scrollHeight;
            // 再補一個影格：此刻 React 才剛把新訊息寫進 DOM，圖片與換行都還沒完成版面計算，
            // scrollHeight 會偏小。後續的圖片載入由 useStickToBottom 接手。
            requestAnimationFrame(() => {
                const e2 = scrollerRef.current;
                if (e2) e2.scrollTop = e2.scrollHeight;
            });
        }
        // pending.length 一併納入：樂觀訊息插入（以及成功後換成真訊息）時都要捲到底，
        // 否則自己剛送出的那則會停在畫面外，正好抵銷掉樂觀更新想帶來的即時感。
    }, [messages?.length, pending.length]);

    // 抓最新一頁、與目前已載入的訊息合併（只新增未見過的，不動已載入的較舊訊息／捲動位置）。
    // 逐字對齊 ParentChatRoomPage／MobileChatRoom 的同名函式，唯一差別是這裡的 messages
    // 初值是 null（＝首次載入還沒回來）：那種情況直接讓初始載入去處理，別讓兩份資料互相蓋。
    // 沒有新訊息也沒有要改成墓碑的訊息時原樣返回 prev：不觸發 re-render ＝不會踩到下方那個
    //「訊息數變動就捲到底」的 effect，老師正在往上翻舊訊息不會被拉走。
    const fetchLatest = useCallback(() => (
        authAPI.chat.messages(convId, { limit: PAGE_SIZE }).then((r) => {
            const data = r.data || [];
            setMessages((prev) => {
                if (prev === null) return prev;
                const known = new Set(prev.map((m) => m.id));
                const fresh = data.filter((m) => !known.has(m.id));
                // 已載入的訊息中「後端說已收回、本地還留著舊內容」的那幾則要換掉：收回事件
                // 同樣走 WS，斷線期間被收回的訊息內容會一直留在畫面上——而這一頁通常開在
                // 大螢幕上，是最容易被旁人瞄到的畫面。
                const recalled = new Map(
                    data.filter((m) => m.is_recalled === true && known.has(m.id)).map((m) => [m.id, m])
                );
                const needPatch = prev.some((m) => m.is_recalled !== true && recalled.has(m.id));
                if (!fresh.length && !needPatch) return prev;
                const base = needPatch
                    ? prev.map((m) => (m.is_recalled !== true && recalled.has(m.id) ? recalled.get(m.id) : m))
                    : prev;
                if (!fresh.length) return base;
                return [...base, ...fresh].sort((a, b) => a.id - b.id);
            });
        }).catch(() => {})
    ), [convId]);

    // WS 每次重新連上就補抓一次。WS 沒有補送機制，斷線那幾秒送達的訊息永久遺失
    //（見 src/hooks/useSocketReconnect.js），這一頁又沒有輪詢，不補就得重開頁面。
    // 只在畫面真的在前景時才補打已讀——桌面常常整天開著但被其他視窗蓋住，那時人沒在看。
    useSocketReconnect(() => {
        fetchLatest();
        if (document.visibilityState === 'visible') {
            authAPI.chat.read(convId)
                .then(() => { notifyChatRead(); onReadRef.current?.(); })
                .catch(() => {});
        }
    });

    // WebSocket 即時：只處理當前這條對話；補進來的訊息若不是自己發的，順手再標記一次已讀（人正在看）。
    // 第二個 handler 收「訊息被收回」：家長（或另一位老師）收回時畫面要立刻變成墓碑。
    // 刻意不補打已讀——收回不是新訊息。
    useChatSocket((payload) => {
        if (payload.conversation_id !== convId) return;
        appendMessage(payload.message);
        if (payload.message?.sender_id !== user?.id) {
            authAPI.chat.read(convId)
                .then(() => { notifyChatRead(); onReadRef.current?.(); })
                .catch(() => {});
        }
    }, (payload) => {
        if (payload.conversation_id !== convId) return;
        replaceMessage(payload.message);
    });

    const loadMore = async () => {
        if (!messages?.length || loadingMore) return;
        setLoadingMore(true);
        try {
            const beforeId = messages[0].id;
            const r = await authAPI.chat.messages(convId, { before_id: beforeId, limit: PAGE_SIZE });
            const older = r.data || [];
            setHasMore(older.length === PAGE_SIZE);
            const fresh = older.filter((m) => !messages.some((p) => p.id === m.id));
            if (fresh.length) {
                skipScrollRef.current = true;
                setMessages((prev) => [...fresh.filter((m) => !(prev || []).some((p) => p.id === m.id)), ...(prev || [])]);
            }
        } catch {
            // 靜默失敗，保留目前已載入的訊息
        }
        // 【不要改寫成 try/finally】實測（2026-08-18）：函式內只要出現 `finally` **語句**，
        // 整個元件就會被 react-hooks 的編譯器分析**靜默跳過**——注入明顯違規也不會被回報，
        // lint 反而顯示 0 error，看起來變好其實是把關消失了。
        // （`.finally()` 方法鏈、沒有 finally 的 try/catch 都正常，只有 finally 語句會觸發。）
        setLoadingMore(false);
    };

    return (
        <Flex direction="column" h="100%" minH={0}>
            {/* 對話標頭：桌面沒有手機的「返回上一頁」，學生/班級名稱要常駐在這裡，
                否則捲到訊息中段就認不出自己在跟誰講話。 */}
            <Flex align="center" gap={3} px={4} py={3} bg="white" borderBottom="1px solid" borderColor="neutral.200" flexShrink={0}>
                {/* 窄螢幕（AdminLayout 在 base 也會渲染這一頁）左右欄是互斥顯示的，要有路徑回列表 */}
                <IconButton
                    display={{ base: 'flex', md: 'none' }}
                    aria-label="返回列表"
                    icon={<FiArrowLeft />}
                    size="sm"
                    variant="ghost"
                    onClick={onBack}
                />
                <Box minW={0} flex="1">
                    <Text fontSize="md" fontWeight="bold" color="neutral.800" noOfLines={1}>
                        {conv.student_name || '（未命名學生）'}
                    </Text>
                    {/* 分串後標頭一定要寫出家長是誰：老師可能同時開著同一位學生的爸爸與媽媽
                        兩個分頁，捲到訊息中段時標頭是唯一還看得到「我在跟誰講話」的地方。
                        傳錯人的代價在親師溝通裡非常高，這一行是防呆而不是裝飾。 */}
                    <Text fontSize="xs" color="neutral.500" noOfLines={1}>
                        {conv.class_name}
                        {conv.parent_name ? `${conv.class_name ? '・' : ''}${conv.parent_name}` : ''}
                    </Text>
                </Box>
            </Flex>

            {/* 訊息畫布：刻意不是白色。MessageBubble 的「對方」氣泡是白底＋淺邊框，
                壓在白色卡片上會整個融掉，要有一層淺米底才分得出氣泡邊界。 */}
            <Box ref={scrollerRef} flex="1" minH={0} overflowY="auto" overscrollBehavior="contain" bg="neutral.50" px={5} py={4}>
                {loading ? (
                    <Center py={16}><Spinner /></Center>
                ) : (
                    <>
                        {hasMore && (
                            <Flex justify="center" mb={3}>
                                <Button size="xs" variant="ghost" color="neutral.500" onClick={loadMore} isLoading={loadingMore}>
                                    載入更早訊息
                                </Button>
                            </Flex>
                        )}
                        {/* orgView：家長靠左、機構端（老師／管理員）一律靠右，並在靠右的氣泡標上
                            回覆者姓名。機構對家長是單一窗口，方向要照「哪一邊」而不是「誰發的」分
                            ——照 isMine 排時，管理員會看到老師和家長的訊息全擠在左邊。
                            詳細理由見 MessageBubble 的 alignRight。 */}
                        <MessageList
                            messages={messages}
                            currentUserId={user?.id}
                            onImageClick={setLightbox}
                            pending={pending}
                            onRetry={handleRetry}
                            onRecall={setRecallTarget}
                            orgView
                        />
                    </>
                )}
            </Box>

            {/* safeAreaBottom={false}：桌面沒有 iOS 安全區，留了只會在輸入列下方多出一塊空白 */}
            <Composer onSend={handleSend} disabled={loading} safeAreaBottom={false} />

            {/* 收回確認：滑過自己發出的訊息時氣泡左側會浮出收回鈕，按下才打開這個框 */}
            <RecallConfirmDialog
                message={recallTarget}
                onClose={() => setRecallTarget(null)}
                onRecalled={replaceMessage}
            />

            {/* 照片放大檢視（沿用手機兩端既有的 react-zoom-pan-pinch lightbox） */}
            <Modal isOpen={!!lightbox} onClose={() => setLightbox(null)} size="full" isCentered>
                <ModalOverlay bg="blackAlpha.900" />
                <ModalContent bg="transparent" boxShadow="none" m={0}>
                    <ModalCloseButton
                        color="white" bg="blackAlpha.600" _hover={{ bg: 'blackAlpha.800' }}
                        borderRadius="full" zIndex={200} size="lg"
                        position="fixed" top="16px" right="16px"
                    />
                    <ModalBody p={0} display="flex" justifyContent="center" alignItems="center">
                        {lightbox && (
                            <TransformWrapper doubleClick={{ mode: 'reset' }}>
                                <TransformComponent
                                    wrapperStyle={{ width: '100vw', height: '100vh' }}
                                    contentStyle={{ width: '100vw', height: '100vh', display: 'flex', alignItems: 'center', justifyContent: 'center' }}
                                >
                                    <Image src={lightbox} w="100vw" maxH="100vh" objectFit="contain" />
                                </TransformComponent>
                            </TransformWrapper>
                        )}
                    </ModalBody>
                </ModalContent>
            </Modal>
        </Flex>
    );
}

function TeacherChatWorkspace() {
    const navigate = useNavigate();
    const { user } = useAuth();
    const toast = useToast();
    const isAdmin = ADMIN_ROLES.includes(user?.role);

    // null ＝ 首次載入尚未完成
    const [conversations, setConversations] = useState(null);
    const [q, setQ] = useState('');
    const [unreadOnly, setUnreadOnly] = useState(false);
    // 【為什麼是衍生值而不是可切換的 state】管理員／超管基本上不帶班，「我的班級」對他而言
    // 幾乎必定是空的——他一進畫面就該直接看到整個機構（user 2026-08-19 回報）。沒有選擇餘地
    // 的開關只是雜訊，所以連切換 UI 一起拿掉。帶班老師維持 'mine'（後端對非管理角色本來就
    // 忽略這個參數，送什麼都一樣，但明確送 'mine' 語意才對得上）。
    // 刻意用衍生值而不是 useState(isAdmin ? 'all' : 'mine')：AuthContext 的 user 初值是 null
    //（2026-07-25 auth 翻新後不再從 localStorage 同步塞入），初次 render 時 isAdmin 必定是
    // false，用初值就會永遠卡在 'mine' 而且沒有任何路徑修正得回來。寫成衍生值則是 user 一到位
    // scope 就跟著變成 'all'，下方那個依賴 scope 的 effect 會自己重抓一次。
    const scope = isAdmin ? 'all' : 'mine'; // 'mine' | 'all'
    const [classId, setClassId] = useState('');   // '' ＝ 全部班級
    // 行政/管理員預設只看未讀（2026-08-31 user 回報：全機構那麼多對話，每次進來都要多點
    // 一下「只看未讀」才看得到真正待回的，很麻煩）。同樣是衍生值會有的問題：user 初值是
    // null，不能在 useState 初值直接判斷。用一次性 effect＋ref 守門——isAdmin 一到位就套
    // 一次預設值，之後使用者自己怎麼切都不會被這裡覆蓋回去。帶班老師維持原預設 false 不變。
    const unreadDefaultApplied = useRef(false);
    useEffect(() => {
        if (unreadDefaultApplied.current || !user) return;
        unreadDefaultApplied.current = true;
        // setState 包一層 .then()：效果本體要同步跑完，setState 才落在非同步 callback 裡，
        // 不會觸發 eslint react-hooks/set-state-in-effect（同樣手法見 useChatEnabled.js 說明）。
        if (isAdmin) Promise.resolve().then(() => setUnreadOnly(true));
    }, [user, isAdmin]);
    const [activeId, setActiveId] = useState(null);
    // 目前開著哪幾個分頁（依開啟順序）。刻意**不**寫進 storage：重新整理回到乾淨狀態才符合
    // 預期（老師 PWA 的收件匣篩選也是同樣的取捨，見 MobileChatList 的 inboxFilters 說明），
    // 而且存下來的 id 在權限或班級異動後可能已經打不開，還原時反而要處理一堆失效分頁。
    const [openIds, setOpenIds] = useState([]);
    // 班級名冊（含還沒有對話的學生）。抓不到就是空陣列＝畫面退回「只有收件匣」的原樣。
    const [roster, setRoster] = useState([]);
    // 分串後同一位學生會有多列（每位家長一列），所以「正在開啟中」要記到列而不是學生，
    // 否則點爸爸那列會讓媽媽那列也一起轉圈。
    const [openingRowKey, setOpeningRowKey] = useState(null);
    // 管理員跨多班時要先問「開在哪一個班」，null＝沒有待選的學生（框關著）
    const [classPickTarget, setClassPickTarget] = useState(null);

    // 收件匣重抓。scope 是唯一會影響「後端回傳哪些對話」的參數；
    // 搜尋與只看未讀刻意留在前端做（見下方 visible 的說明），所以這裡不隨它們變動。
    const fetchInbox = useCallback(() => (
        authAPI.chat.inbox({ scope })
            .then((r) => setConversations(r.data || []))
            .catch(() => setConversations([]))
    ), [scope]);

    // 這裡刻意「再寫一次」而不是直接呼叫上面的 fetchInbox：scope 會在登入者身分確認後由
    // 'mine' 變成 'all'（見上方說明），兩次請求的回應順序不保證，先發的那次可能後到而覆蓋掉
    // 正確的清單。用 alive 旗標把已經失效的回應丟掉；背景重抓（WS/已讀）沒有這個問題，
    // 故沿用 fetchInbox。
    useEffect(() => {
        let alive = true;
        authAPI.chat.inbox({ scope })
            .then((r) => { if (alive) setConversations(r.data || []); })
            .catch(() => { if (alive) setConversations([]); });
        return () => { alive = false; };
    }, [scope]);

    // 任何 chat_message 事件都重抓收件匣（未讀數／最後訊息／排序都可能變）；
    // 量體小（無分頁），整份重抓比逐筆合併簡單可靠。
    // 收回事件同樣重抓，而且對這一頁特別重要：後端收回時會把該對話的 last_message_preview
    // 改成「[訊息已收回]」，不重抓的話**被收回的內文會繼續留在左欄摘要上**——而這一頁通常
    // 是開在大螢幕上、最容易被旁人瞄到的畫面。未讀數也會跟著變（後端不把已收回的算成未讀）。
    useChatSocket(fetchInbox, fetchInbox);

    // WS 每次重新連上也重抓一次：斷線期間到達的 chat_message 是**永久遺失**的（WS 不補送），
    // 少了這次重抓，左欄的未讀數、最後訊息摘要與排序會一直停在斷線前的狀態，老師從列表
    // 完全看不出「有新訊息進來過」。這一頁沒有輪詢也沒有回前景 refetch，這是唯一的補救路徑。
    useSocketReconnect(fetchInbox);

    // 班級名冊：/chat/inbox 只回「已經存在的對話」，而對話只在家長開 App、聯絡簿 fanout、
    // 群發 fanout 三個時機才會被建出來——「家長從沒開過 App、且該班沒發過聯絡簿」的學生
    // 因此完全不會出現在左欄，偏偏那正是最需要老師主動聯繫的一群。這份名冊就是補這個洞，
    // 讓他們也能被列出來、點一下就用 POST /chat/students/{id}/conversation 開一條。
    //
    // 【2026-08-19：資料來源從 /teachers/students/ 換成 /chat/roster】
    // 原本借用學生管理的端點，並在這裡註解「權限與開對話端點同一套，列得出來的就開得成」。
    // 那句話對帶班老師成立、**對管理員三個方向都不成立**（sqlite rig 實測）：沒有主班只用
    // V2 關聯掛班的學生會整批消失、超管會拿到別機構點下去必定 403 的學生、機構未設定的
    // 管理員帳號則是 400 被下面的 catch 靜默吞成空名冊（整區無聲消失）。
    // /chat/roster 由 chat 自己回答，用的就是開對話端點的那組函式，不變式由結構保證。
    //
    // 附帶好處：它只回 { student_id, student_name, classes }，不再需要 fetchAllPages 把
    // 兩百多筆完整 StudentOut（含家長、班級、機構）整份拉下來；搜尋也仍然在前端就地做
    //（收件匣本來就是，且這支端點沒有 search 參數可以踩到「帶 search 就放寬成全機構」的坑）。
    //
    // 為什麼不隨 scope 重抓：這支端點沒有 scope 參數，權限固定依角色。
    useEffect(() => {
        let alive = true;
        authAPI.chat.roster()
            .then((r) => { if (alive) setRoster(r.data || []); })
            // 靜默失敗：名冊只是「多列出幾個還沒對話的學生」，抓不到就退回原本只有收件匣的
            // 樣子，不該讓既有對話跟著出不來。
            .catch(() => { if (alive) setRoster([]); });
        return () => { alive = false; };
    }, []);

    const list = useMemo(() => conversations || [], [conversations]);

    // 班級選項＝已載入的對話 ＋ 名冊裡的班，兩邊合併，都不另外打 /classes/lite
    //（收件匣每筆對話與名冊每位學生本來就帶 class_id / class_name，權限也都已由後端過濾過）。
    //
    // 【為什麼現在要把「沒有任何對話的班」也列進來 —— 與這段原本的註解相反】
    // 原本刻意不列，理由是「老師端沒有任何端點可以主動開一條新對話，選了就是一片空白而且
    // 無路可走」。POST /chat/students/{id}/conversation 上線後這個前提不成立了：選了那個班，
    // 下方會列出該班還沒有對話的學生，點一下就開得起來——正是原註解說做不到的事。
    // 依班名排序讓選單順序穩定（後端是依最後訊息時間排，位置會一直變）。
    const classOptions = useMemo(() => {
        const seen = new Map();
        list.forEach((c) => {
            if (c.class_id != null && !seen.has(c.class_id)) seen.set(c.class_id, c.class_name || `班級 ${c.class_id}`);
        });
        roster.forEach((s) => {
            (s.classes || []).forEach((c) => { if (!seen.has(c.id)) seen.set(c.id, c.name); });
        });
        return [...seen.entries()]
            .map(([id, name]) => ({ id, name }))
            .sort((a, b) => a.name.localeCompare(b.name, 'zh-Hant'));
    }, [list, roster]);

    // 切 scope 後原本選的班級可能整個消失。用「衍生值」修正而不是 effect+setState：
    // effect 是 render 後才跑，那一幀會先閃一次空清單，而且會踩到 set-state-in-effect。
    const activeClassId = classId && classOptions.some((c) => String(c.id) === String(classId)) ? classId : '';

    // 搜尋與未讀篩選都在前端做：收件匣是一次抓完整份（後端無分頁），前端過濾不需要來回網路，
    // 也不會每打一個字就重打一次 API。條件與後端一致（姓名子字串比對 / unread > 0）。
    const visible = useMemo(() => {
        const keyword = q.trim();
        return list.filter((c) => {
            if (activeClassId && String(c.class_id) !== String(activeClassId)) return false;
            if (unreadOnly && !(c.unread > 0)) return false;
            // 分串後也比對家長姓名：老師要找的常常是「王媽媽」而不是學生本名，
            // 而同一位學生現在有兩列，只靠學生姓名搜尋會兩列一起出現、篩不掉。
            // 條件與後端 /chat/inbox 的 q 過濾一致。
            if (keyword
                && !(c.student_name || '').includes(keyword)
                && !(c.parent_name || '').includes(keyword)) return false;
            return true;
        });
    }, [list, activeClassId, unreadOnly, q]);

    // 名冊裡「還沒有對話」的（學生 × 家長），接在收件匣清單下方。
    //
    // 【2026-08-19：一列＝一位家長，不再是一位學生】對話的唯一鍵已含 parent_id。若這裡
    // 仍以學生為單位判定「有沒有對話」，一位媽媽已開過、爸爸還沒開的學生會被判成「已有對話」
    // 而整列消失——爸爸那條就此再也沒有任何入口，老師只能等他自己開 App。
    // 家長身分直接來自名冊（後端 /chat/roster 的 parents），所以是**明確已知**的，
    // 不需要再跳一個「要開給誰」的選擇框。
    //
    // 【判定為什麼要看 學生×班級×家長】選了班級時若只比對 student_id，一位在 A 班已有對話、
    // B 班還沒有的學生，選 B 班會被誤判成「已有對話」而整個消失。沒選班級時無法決定要開哪
    // 一個班（交給下方的 handlePendingClick／後端挑），此時退回用 (學生, 家長) 判定：
    // 這位家長只要在任何一個班已經有對話，就已經在上方收件匣裡了，不該重複列一次。
    //
    // 【沒有綁定家長的學生】仍然列出來（parentId 為 null），老師需要知道「這個孩子聯絡不到」；
    // 直接從名冊消失只會讓人以為系統漏了誰。點下去會拿到後端一句話說明的 400。
    //
    // 【已知限制】判定的依據是「目前這份收件匣」。行政/管理員把範圍切在『我的班級』時，
    // 收件匣只含自己帶的班（多半是空的），但名冊是整個機構——於是已經有對話的學生也會被
    // 列進「尚未開始對話」。這只是標籤不準：點下去後端是冪等的，會直接回既有那一條、
    // 不會開出重複的對話。要看準確的清單就把範圍切到『全機構』。
    //（不特別為此擋掉：擋掉等於行政在『我的班級』下完全無法主動開啟對話，代價更大。）
    const pendingStudents = useMemo(() => {
        // 「還沒有對話」＝不可能有未讀。開著「只看未讀」時列出來只會干擾。
        if (unreadOnly) return [];
        const convTriples = new Set(list.map((c) => `${c.student_id}:${c.class_id}:${c.parent_id}`));
        const convParents = new Set(list.map((c) => `${c.student_id}:${c.parent_id}`));
        const keyword = q.trim();
        const rows = [];
        roster.forEach((s) => {
            // 關鍵字也比對家長姓名，與上方 visible 的規則一致
            const parents = s.parents || [];
            const nameHit = !keyword
                || (s.student_name || '').includes(keyword)
                || parents.some((p) => (p.name || '').includes(keyword));
            if (!nameHit) return;
            // 後端已保證每一列至少有一個開得起來的班（沒有班的學生根本不會出現在名冊裡）
            const classes = s.classes || [];
            if (!classes.length) return;
            // 尚未綁定家長的學生用一列 parentId=null 代表（點下去由後端回 400 說明原因）
            const parentSlots = parents.length ? parents : [{ id: null, name: null }];
            parentSlots.forEach((p) => {
                if (activeClassId) {
                    const hit = classes.find((c) => String(c.id) === String(activeClassId));
                    if (!hit || convTriples.has(`${s.student_id}:${hit.id}:${p.id}`)) return;
                    rows.push({
                        studentId: s.student_id, name: s.student_name,
                        parentId: p.id, parentName: p.name,
                        classId: hit.id, className: hit.name, classes,
                    });
                } else {
                    if (convParents.has(`${s.student_id}:${p.id}`)) return;
                    // classId 留空＝還沒決定要開哪一班；點下去時再由 handlePendingClick 決定
                    //（只有一班就直接用那一班，管理員跨多班則跳出班級選擇）。
                    // 班名全部列出來，老師才知道這位學生同時在哪幾個班。
                    rows.push({
                        studentId: s.student_id, name: s.student_name,
                        parentId: p.id, parentName: p.name,
                        classId: null, className: classes.map((c) => c.name).join('、'), classes,
                    });
                }
            });
        });
        // 同一位學生的家長要相鄰，否則兩列會被拆到清單的兩端
        return rows.sort((a, b) => (
            (a.name || '').localeCompare(b.name || '', 'zh-Hant')
            || (a.parentName || '').localeCompare(b.parentName || '', 'zh-Hant')
        ));
    }, [roster, list, activeClassId, unreadOnly, q]);

    const activeConv = useMemo(() => list.find((c) => c.id === activeId) || null, [list, activeId]);

    // 分頁列要顯示的那幾列。對應不到收件匣就跳過（而不是渲染一個空白分頁）：權限異動後
    // 某條對話可能已經不在清單裡。留在 openIds 裡不清掉是刻意的——清掉要寫在 effect 裡，
    // 那正是本檔 react-hooks 規則擋的 set-state-in-effect；而它若又回到清單裡，分頁本來
    // 就該原地回來。
    const openTabs = useMemo(
        () => openIds.map((id) => list.find((c) => c.id === id)).filter(Boolean),
        [openIds, list],
    );

    // 開一個分頁並切過去。已經開著就只是切過去，不會重複開第二個——所以左欄點選、分頁點選、
    // 名冊開新對話三個入口全部走這一支，「切到某條對話」只有一種行為。
    const openTab = (id) => {
        // 未讀數先在本地歸零：後端的已讀 API 與收件匣重抓要一個來回，等它回來紅字才消失
        // 會讓「我明明點開了」與畫面對不上。真值由 ChatRoomPane 的 onRead 重抓覆蓋。
        setConversations((prev) => (prev || []).map((c) => (c.id === id ? { ...c, unread: 0 } : c)));
        setOpenIds((prev) => {
            if (prev.includes(id)) return prev;
            const next = [...prev, id];
            if (next.length <= MAX_TABS) return next;
            // 滿了要擠掉一個：挑最舊的，但不能擠掉剛開的這一個、也不擠掉剛剛還在看的那一個
            //（「點一位學生，結果我上一秒還在回的那條對話消失了」會讓人以為東西被弄丟了）。
            const victim = next.find((x) => x !== id && x !== activeId) ?? next.find((x) => x !== id);
            return next.filter((x) => x !== victim);
        });
        setActiveId(id);
    };

    // 關掉一個分頁。關的是正在看的那一個時，接手右邊那一個（沒有就左邊），比照瀏覽器分頁——
    // 直接丟回空白畫面會逼老師再去左欄找一次。全部關完才回到「從左側選一位學生」的空狀態。
    const closeTab = (id) => {
        const idx = openIds.indexOf(id);
        const rest = openIds.filter((x) => x !== id);
        setOpenIds(rest);
        // 移除之後原本 idx 這格就是原本右邊那一個；idx 已是最後一格時退回左邊那一個
        if (id === activeId) setActiveId(rest[idx] ?? rest[idx - 1] ?? null);
    };

    // 開（或取得）對話並直接選它。後端冪等，重複點不會開出第二條。
    // parentId 一律從名冊那一列帶過去（分串後它是對話身分的一部分）——不帶的話後端會退回
    // 主要家長，等於老師點的是「爸爸」那一列卻開出媽媽的對話。
    const openWithStudent = async (row, classId) => {
        if (openingRowKey) return;   // 連點防呆：這支會寫 DB，讓它一次只跑一條
        setClassPickTarget(null);
        setOpeningRowKey(pendingRowKey(row));
        try {
            const r = await authAPI.chat.openStudentConversation(row.studentId, classId, row.parentId);
            const conv = r.data;
            // 先把回傳的那條塞進本地清單並選起來，老師點一下就進得去；不等重抓完成
            //（後端已經回了完整的一列，格式與 /inbox 相同，本來就夠用）。
            // 用 filter 去掉同 id 再插到最前面：這支是冪等的，回來的有可能是既有那一條。
            setConversations((prev) => [conv, ...(prev || []).filter((c) => c.id !== conv.id)]);
            openTab(conv.id);
            // 再背景重抓一次，讓排序與未讀數回到後端的真值（本地是硬插到最前面）
            fetchInbox();
        } catch (err) {
            // 後端的 detail 是完整一句話（「無權與此學生開啟對話」「此學生未在任何班級…」），
            // 直接顯示；這些情況老師改不了系統但看得懂原因，籠統的「失敗」反而讓人一直重試。
            toast({
                title: '無法開啟對話',
                description: err.response?.data?.detail || '請稍後再試',
                status: 'error',
                duration: 3000,
                isClosable: true,
            });
        }
        // 【不要改寫成 try/finally】實測（2026-08-18）：函式內只要出現 `finally` **語句**，
        // 整個元件就會被 react-hooks 的編譯器分析**靜默跳過**——注入明顯違規也不會被回報，
        // lint 反而顯示 0 error，看起來變好其實是把關消失了。
        // （`.finally()` 方法鏈、沒有 finally 的 try/catch 都正常，只有 finally 語句會觸發。）
        setOpeningRowKey(null);
    };

    // 點名冊裡的學生 → 先決定要開在哪一個班，再交給 openWithStudent。
    // 【為什麼管理員一定要自己選】班級是**權限與通知的路由依據**：後端會把該班的帶班老師
    // 拉進來收推播（chat.py `_teacher_ids_of_class`）、WS 即時推送也走 `broadcast_to_class`。
    // 不帶 class_id 時後端會自動挑一個班（`_pick_conversation_class_id`），對管理員而言那等於
    // 系統替他決定了「誰會開始收到這個家庭的訊息」而他自己不會知道（user 2026-08-19 拍板要明選）。
    // 帶班老師維持現狀：後端給他的候選班本來就只有自己帶的班，挑到哪一個都是自己的班。
    const handlePendingClick = (row) => {
        if (openingRowKey) return;
        // 已經用左上角班級下拉篩過（row.classId 有值）＝班級早就明確了，不必再問一次
        if (row.classId != null) return openWithStudent(row, row.classId);
        // 只有一個候選班：沒有東西可選，直接明講那一班的 id
        //（結果與讓後端自動挑相同，但不依賴那條規則——見上方說明）
        if ((row.classes || []).length === 1) return openWithStudent(row, row.classes[0].id);
        if (isAdmin) return setClassPickTarget(row);
        return openWithStudent(row, null);
    };

    const loading = conversations === null;

    return (
        <Flex direction="column" h="100%" minH="520px" gap={4}>
            <Flex align="center" justify="space-between" gap={3} flexShrink={0}>
                <Box>
                    <Heading size="md" color="neutral.800">親師對話</Heading>
                    <Text fontSize="xs" color="neutral.500" mt={0.5}>
                        與家長一對一溝通；訊息與老師 App、家長 App 同步
                    </Text>
                </Box>
                {/* 【為什麼群發只留這一顆】原本對話標頭還有一顆「群發到這個班」，兩顆長得幾乎
                    一樣（同色同圖示、標籤都以「群發」開頭），差別只在有沒有帶班級——使用者
                    看到的就是同一個功能出現兩次（user 2026-08-19 回報）。
                    留頁面層這顆而不是對話裡那顆，理由有二：
                    ① 對話裡那顆只有「已經開著某條對話」時才存在。若只留它，老師想單純發一則
                       班級公告，得先隨便點開一位家長的私訊才找得到入口。
                    ② 一對一對話的標頭本來是在講「這個家庭」，把一個「發給全班」的動作放在那裡，
                       最壞的情況是老師以為自己在回這位家長。
                    對話裡那顆唯一的好處（預選班級）不必跟著一起丟掉：有開著對話時就把它的
                    class_id 一併帶過去，群發頁的班級下拉會預選好、也看得見可以改。 */}
                <Button
                    size="sm"
                    colorScheme="brand"
                    variant="outline"
                    leftIcon={<Icon as={FiSend} />}
                    onClick={() => navigate(
                        '/teacher/broadcast',
                        activeConv ? { state: { classId: activeConv.class_id } } : undefined,
                    )}
                >
                    整班群發
                </Button>
            </Flex>

            <Flex flex="1" minH={0} gap={4} align="stretch">
                {/* ── 左欄（1/4 寬）：上方班級下拉、下方學生列表 ── */}
                <Flex
                    direction="column"
                    w={{ base: '100%', md: '25%' }}
                    minW={{ md: '250px' }}
                    maxW={{ md: '360px' }}
                    flexShrink={0}
                    bg="white"
                    border="1px solid"
                    borderColor="neutral.200"
                    borderRadius="xl"
                    overflow="hidden"
                    // 窄螢幕沒有並排的空間：選了對話就只顯示右欄，右欄標頭有返回鍵
                    display={{ base: activeConv ? 'none' : 'flex', md: 'flex' }}
                >
                    <VStack spacing={2} align="stretch" px={3} py={3} borderBottom="1px solid" borderColor="neutral.200" flexShrink={0}>
                        <Select
                            size="sm"
                            value={activeClassId}
                            onChange={(e) => setClassId(e.target.value)}
                            borderRadius="lg"
                            bg="neutral.50"
                            icon={<Icon as={FiChevronDown} />}
                            isDisabled={classOptions.length === 0}
                        >
                            <option value="">全部班級（{list.length}）</option>
                            {classOptions.map((c) => (
                                <option key={c.id} value={c.id}>
                                    {c.name}（{list.filter((v) => v.class_id === c.id).length}）
                                </option>
                            ))}
                        </Select>

                        <InputGroup size="sm">
                            <InputLeftElement pointerEvents="none">
                                <Icon as={FiSearch} color="neutral.400" />
                            </InputLeftElement>
                            <Input
                                placeholder="搜尋學生姓名"
                                value={q}
                                onChange={(e) => setQ(e.target.value)}
                                borderRadius="lg"
                                bg="neutral.50"
                            />
                        </InputGroup>

                        <Flex justify="space-between" align="center" gap={2}>
                            <HStack spacing={2}>
                                <Switch size="sm" isChecked={unreadOnly} onChange={(e) => setUnreadOnly(e.target.checked)} colorScheme="brand" />
                                <Text fontSize="xs" color="neutral.500">只看未讀</Text>
                            </HStack>
                        </Flex>
                    </VStack>

                    <Box flex="1" minH={0} overflowY="auto">
                        {loading ? (
                            <Center py={16}><Spinner /></Center>
                        ) : visible.length === 0 && pendingStudents.length === 0 ? (
                            <Center py={16} px={4} flexDirection="column" textAlign="center">
                                <Icon as={FiInbox} boxSize="28px" color="neutral.300" mb={2} />
                                {/* 空狀態要說對是哪一種空：選了班級沒東西、搜尋沒結果、整個收件匣是空的，處理方式不同 */}
                                <Text fontSize="sm" color="neutral.500">
                                    {q.trim() ? '找不到符合的學生'
                                        : activeClassId ? '這個班級沒有符合的對話'
                                            : unreadOnly ? '沒有未讀訊息'
                                                : '目前沒有對話'}
                                </Text>
                            </Center>
                        ) : (
                            <>
                                {visible.map((conv) => (
                                    <ConversationRow
                                        key={conv.id}
                                        conv={conv}
                                        isActive={conv.id === activeId}
                                        onClick={() => openTab(conv.id)}
                                    />
                                ))}

                                {/* 還沒有對話的學生放在既有對話「之後」：上面那段是老師每天要回的，
                                    永遠不該被名冊擠下去。 */}
                                {pendingStudents.length > 0 && (
                                    <>
                                        <Box px={3} py={1.5} bg="neutral.50" borderY="1px solid" borderColor="neutral.200">
                                            <Text fontSize="10px" fontWeight="bold" color="neutral.500">
                                                尚未開始對話（{pendingStudents.length}）
                                            </Text>
                                            {/* 講清楚點下去會發生什麼事：這是唯一會「主動建立」東西的入口 */}
                                            <Text fontSize="10px" color="neutral.400">
                                                點一下即可開始，家長下次開啟 App 就會看到
                                            </Text>
                                        </Box>
                                        {pendingStudents.map((row) => (
                                            <StudentRow
                                                key={pendingRowKey(row)}
                                                row={row}
                                                isOpening={openingRowKey === pendingRowKey(row)}
                                                onClick={() => handlePendingClick(row)}
                                            />
                                        ))}
                                    </>
                                )}
                            </>
                        )}
                    </Box>
                </Flex>

                {/* ── 右欄：完整對話框 ── */}
                <Flex
                    direction="column"
                    flex="1"
                    minW={0}
                    bg="white"
                    border="1px solid"
                    borderColor="neutral.200"
                    borderRadius="xl"
                    overflow="hidden"
                    display={{ base: activeConv ? 'flex' : 'none', md: 'flex' }}
                >
                    {openTabs.length > 0 && (
                        <ChatTabBar
                            tabs={openTabs}
                            activeId={activeId}
                            onSelect={openTab}
                            onClose={closeTab}
                        />
                    )}

                    {/* 【為什麼背景分頁不掛載，只留「開著哪幾條」】
                        ChatRoomPane 一掛載就會 ① POST read 把該對話標成已讀 ② 訂閱 WS，而且
                        每收到一則屬於自己那條對話的訊息就再標一次已讀（理由是「人正在看」）。
                        若把沒在看的分頁也掛著，家長傳進來的訊息會在老師根本沒看的情況下被標成
                        已讀，未讀數就此無聲消失——而未讀正是分頁最需要保住的資訊。
                        另外 useStickToBottom 是靠 scrollHeight/clientHeight 判斷要不要釘底，
                        display:none 的容器兩個值都是 0，釘底邏輯會失準。
                        所以分頁只記「開著哪幾條」，切過去時照既有的 key 重建，行為與改用分頁
                        之前「換一條對話」完全相同——那幾套捲動／樂觀更新／已讀邏輯一行都沒動到。
                        代價：切回某個分頁要重抓一次最新訊息，捲動位置回到最新那則而不是離開時
                        停的地方。對聊天室而言最新訊息本來就是該停的位置，這個代價可以接受。 */}
                    {activeConv ? (
                        // key＝換一條對話就整個重建，state 天然歸零（見 ChatRoomPane 說明）
                        <ChatRoomPane
                            key={activeConv.id}
                            conv={activeConv}
                            onBack={() => setActiveId(null)}
                            onRead={fetchInbox}
                        />
                    ) : (
                        <Center h="100%" flexDirection="column" gap={2} px={6} textAlign="center">
                            <Icon as={FiMessageSquare} boxSize="36px" color="neutral.300" />
                            <Text fontSize="sm" color="neutral.500">從左側選一位學生開始對話</Text>
                            <Text fontSize="xs" color="neutral.400">
                                對話會依家長最後傳訊時間排序，未讀的會標示在學生姓名旁
                            </Text>
                        </Center>
                    )}
                </Flex>
            </Flex>

            {/* 管理員點了跨多班的學生才會打開（只有一班或已用班級下拉篩過時不會問） */}
            <ClassPickerDialog
                student={classPickTarget}
                onClose={() => setClassPickTarget(null)}
                onPick={(classId) => openWithStudent(classPickTarget, classId)}
            />
        </Flex>
    );
}

export default function TeacherChatPage() {
    const { enabled, loading } = useChatEnabledStatus();
    // 旗標查完前先 Spinner：先閃一次「尚未啟用」再跳成真 UI，會讓人以為功能壞了
    if (loading) return <Center h="100%" minH="320px"><Spinner /></Center>;
    return enabled ? <TeacherChatWorkspace /> : <ChatDisabledNotice />;
}
